import { Bell, Calendar, ChevronRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { Avatar } from '@/components/ui/Avatar';
import { useCurrentUser } from '@/features/auth/session';
import { REQUEST_NAV } from './navItems';

const TITLES: Record<string, { title: string; breadcrumb: string[] }> = {
  '/dashboard':         { title: 'Dashboard',          breadcrumb: ['Overview'] },
  '/requests':          { title: 'All requests',       breadcrumb: ['Requests', 'All'] },
  '/customers':         { title: 'Customers',          breadcrumb: ['Customers'] },
  '/payments':          { title: 'Payments',           breadcrumb: ['Payments'] },
  '/banks':             { title: 'Banks',              breadcrumb: ['Banks'] },
  '/messages':          { title: 'Messages',           breadcrumb: ['Messages'] },
  '/notifications-log': { title: 'Notifications log',  breadcrumb: ['Notifications'] },
  '/settings':          { title: 'Settings',           breadcrumb: ['Settings'] },
};

export function TopBar() {
  const location = useLocation();
  const user = useCurrentUser();
  const name = [user?.usrFirstName, user?.usrLastName]
    .filter(Boolean)
    .join(' ') || user?.usrMobileNumber || 'Back office';

  const { title, breadcrumb } = useMemo(() => {
    // Per-request-type pages first
    const sub = REQUEST_NAV.find((r) => location.pathname.endsWith(r.path));
    if (location.pathname.startsWith('/requests/') && sub) {
      return { title: sub.label, breadcrumb: ['Requests', sub.label] };
    }
    if (location.pathname.startsWith('/customers/')) {
      return { title: 'Customer detail', breadcrumb: ['Customers', 'Detail'] };
    }
    return TITLES[location.pathname] ?? { title: 'KIWASCO', breadcrumb: [] };
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-30 h-14 bg-white border-b border-line flex items-center px-6 gap-4">
      <div className="flex-1 min-w-0">
        <h1 className="text-h2 text-ink truncate">{title}</h1>
        {breadcrumb.length > 0 && (
          <nav className="flex items-center gap-1 text-caption text-ink-muted -mt-0.5">
            {breadcrumb.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
                <span>{c}</span>
              </span>
            ))}
          </nav>
        )}
      </div>

      <button
        type="button"
        className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-pill border border-line bg-white text-caption text-ink hover:bg-canvas"
      >
        <Calendar className="h-3.5 w-3.5" />
        Last 7 days
      </button>

      <button
        type="button"
        className="relative h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-canvas"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4 text-ink-muted" />
        <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-status-error border-2 border-white" />
      </button>

      <Avatar name={name} size="sm" />
    </header>
  );
}
